import { AxiosResponse } from 'axios';
import apiClient from '../client';
import { ApiResponseDto } from 'interfaces/apiResponse';
import {
  TenantLogoUploadResponse,
  TenantLogoResponse,
  TenantSettingsResponse,
  TenantSettingsUpdateRequest,
  TenantOnboardRequest,
  TenantOnboardResponse,
  TenantListItem,
  TenantUpdateFeaturesRequest,
} from 'interfaces/tenant';

interface TenantService {
  uploadLogo(file: File): Promise<TenantLogoUploadResponse>;
  getLogo(): Promise<TenantLogoResponse>;
  getSettings(): Promise<TenantSettingsResponse>;
  updateSettings(data: TenantSettingsUpdateRequest): Promise<TenantSettingsResponse>;
  onboard(data: TenantOnboardRequest): Promise<TenantOnboardResponse>;
  listTenants(): Promise<TenantListItem[]>;
  updateFeatures(alias: string, data: TenantUpdateFeaturesRequest): Promise<TenantListItem>;
}

const endpoints = {
  logo: '/tenants/logo',
  settings: '/tenants/current/settings',
  updateSettings: '/tenants',
  admin: '/admin/tenants',
};

export const tenantsService: TenantService = {
  uploadLogo: async (file: File): Promise<TenantLogoUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    const response: AxiosResponse<ApiResponseDto<TenantLogoUploadResponse>> = await apiClient.post(endpoints.logo, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },

  getLogo: async (): Promise<TenantLogoResponse> => {
    const response: AxiosResponse<ApiResponseDto<TenantLogoResponse>> = await apiClient.get(endpoints.logo);

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },

  getSettings: async (): Promise<TenantSettingsResponse> => {
    const response: AxiosResponse<ApiResponseDto<TenantSettingsResponse>> = await apiClient.get(endpoints.settings);

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },

  updateSettings: async (data: TenantSettingsUpdateRequest): Promise<TenantSettingsResponse> => {
    const response: AxiosResponse<ApiResponseDto<TenantSettingsResponse>> = await apiClient.patch(endpoints.updateSettings, data);

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },

  onboard: async (data: TenantOnboardRequest): Promise<TenantOnboardResponse> => {
    const response: AxiosResponse<ApiResponseDto<TenantOnboardResponse>> = await apiClient.post(endpoints.admin, data);

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },

  listTenants: async (): Promise<TenantListItem[]> => {
    const response: AxiosResponse<ApiResponseDto<TenantListItem[]>> = await apiClient.get(endpoints.admin);

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },

  updateFeatures: async (alias: string, data: TenantUpdateFeaturesRequest): Promise<TenantListItem> => {
    const response: AxiosResponse<ApiResponseDto<TenantListItem>> = await apiClient.put(`${endpoints.admin}/${alias}`, data);

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },
};
